import React, { useState } from 'react';
import { CLINICAL_SEVERITY_LEVELS, NON_CLINICAL_SEVERITY_LEVELS } from '../lib/constants';
import { SeverityGuideModal } from './risk/SeverityGuideModal';

/**
 * ฟังก์ชันช่วยค้นหาข้อมูลระดับความรุนแรง
 */
export const getSeverityInfo = (levelCode, domain = null) => {
  if (!levelCode) return null;
  const codeStr = String(levelCode).trim().toUpperCase();

  // ตรวจสอบจาก domain ถ้ามีการส่งเข้ามา หรือ auto-detect
  const isAlpha = /^[A-I]$/i.test(codeStr);
  const targetLevels = (domain === 'clinical' || domain === 'cssd' || isAlpha)
    ? CLINICAL_SEVERITY_LEVELS
    : NON_CLINICAL_SEVERITY_LEVELS;

  const found = targetLevels.find((item) => item.code.toUpperCase() === codeStr);
  if (found) return found;

  // Fallback ค้นหาข้ามชุด
  return (
    CLINICAL_SEVERITY_LEVELS.find((i) => i.code.toUpperCase() === codeStr) ||
    NON_CLINICAL_SEVERITY_LEVELS.find((i) => i.code.toUpperCase() === codeStr) || {
      code: codeStr,
      name: `ระดับ ${codeStr}`,
      description: 'ระดับความรุนแรง',
      group: 'ทั่วไป',
      color: '#64748b',
      badgeBg: '#f1f5f9',
      badgeBorder: '#cbd5e1'
    }
  );
};

/**
 * คอมโพเนนต์แสดง Badge ระดับความรุนแรง (DentalSeverityBadge)
 */
export const DentalSeverityBadge = ({
  level,
  domain = null,
  size = 'md',
  showLabel = true,
  showDescriptionTooltip = true
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const info = getSeverityInfo(level, domain);

  if (!info) {
    return (
      <span style={{
        padding: '3px 8px',
        borderRadius: '6px',
        fontSize: '0.78em',
        backgroundColor: '#f1f5f9',
        color: '#64748b'
      }}>
        -
      </span>
    );
  }

  const isSmall = size === 'sm';
  const isLarge = size === 'lg';

  const badgePadding = isSmall ? '2px 6px' : isLarge ? '6px 14px' : '4px 10px';
  const fontSize = isSmall ? '0.75em' : isLarge ? '0.95em' : '0.82em';

  return (
    <div
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: badgePadding,
          borderRadius: '9999px',
          backgroundColor: info.badgeBg || '#f1f5f9',
          border: `1.5px solid ${info.badgeBorder || info.color || '#cbd5e1'}`,
          color: info.textColor || info.color || '#1e293b',
          fontSize,
          fontWeight: '700',
          letterSpacing: '0.02em',
          cursor: showDescriptionTooltip ? 'help' : 'default',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          whiteSpace: 'nowrap',
          transition: 'transform 0.15s ease'
        }}
      >
        <span
          style={{
            width: isSmall ? '8px' : '10px',
            height: isSmall ? '8px' : '10px',
            borderRadius: '50%',
            backgroundColor: info.color || '#64748b',
            display: 'inline-block',
            flexShrink: 0
          }}
        />
        <span>ระดับ {info.code}</span>
        {showLabel && info.group && !isSmall && (
          <span
            style={{
              opacity: 0.85,
              fontWeight: '500',
              fontSize: '0.9em',
              borderLeft: `1px solid ${info.badgeBorder || info.color}`,
              paddingLeft: '6px',
              marginLeft: '2px'
            }}
          >
            {info.group.split(' ')[0]}
          </span>
        )}
      </span>

      {/* Tooltip รายละเอียด */}
      {showDescriptionTooltip && showTooltip && (
        <div
          style={{
            position: 'absolute',
            bottom: '120%',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            backgroundColor: '#0f172a',
            color: '#f8fafc',
            padding: '10px 14px',
            borderRadius: '10px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.4)',
            width: '280px',
            pointerEvents: 'none',
            fontSize: '0.82em',
            lineHeight: '1.45',
            textAlign: 'left'
          }}
        >
          <div style={{ fontWeight: '700', color: info.color, marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>ระดับ {info.code}: {info.name}</span>
          </div>
          <div style={{ color: '#cbd5e1', fontSize: '0.92em', marginBottom: '6px' }}>
            {info.description}
          </div>
          {(info.action_timeline || info.reporting_timeline || info.rca_type) && (
            <div
              style={{
                marginTop: '6px',
                paddingTop: '6px',
                borderTop: '1px solid rgba(255, 255, 255, 0.15)',
                display: 'flex',
                flexDirection: 'column',
                gap: '3px',
                fontSize: '0.88em'
              }}
            >
              {info.action_timeline && (
                <div style={{ color: '#7dd3fc' }}>
                  ⏱️ <strong>รักษา/แก้ไข:</strong> {info.action_timeline}
                </div>
              )}
              {info.reporting_timeline && (
                <div style={{ color: '#fde68a' }}>
                  📢 <strong>รายงาน:</strong> {info.reporting_timeline}
                </div>
              )}
              {info.rca_type && (
                <div style={{ color: info.rca_required ? '#fca5a5' : '#86efac', fontWeight: '700' }}>
                  {info.rca_required ? '⚠️' : '🛡️'} <strong>RCA:</strong> {info.rca_type}
                  {info.rca_timeline && info.rca_required ? ` (${info.rca_timeline})` : ''}
                </div>
              )}
            </div>
          )}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              borderWidth: '6px',
              borderStyle: 'solid',
              borderColor: '#0f172a transparent transparent transparent'
            }}
          />
        </div>
      )}
    </div>
  );
};

/**
 * คอมโพเนนต์ตัวเลือกและแสดงความรุนแรงแบบ Interactive (SeveritySelector)
 */
export const SeveritySelector = ({
  domain = 'clinical',
  value = '',
  onChange = () => {},
  disabled = false
}) => {
  const [showGuideModal, setShowGuideModal] = useState(false);
  const isClinicalOrCssd = domain === 'clinical' || domain === 'cssd';
  const levels = isClinicalOrCssd ? CLINICAL_SEVERITY_LEVELS : NON_CLINICAL_SEVERITY_LEVELS;

  return (
    <div style={{ width: '100%' }}>
      {/* Header bar with Guide Button */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
          flexWrap: 'wrap',
          gap: '6px'
        }}
      >
        <span style={{ fontSize: '0.84em', color: '#64748b', fontWeight: '600' }}>
          เลือกระดับความรุนแรง ({isClinicalOrCssd ? 'Level A - I' : 'Level 1 - 5'})
        </span>
        <button
          type="button"
          onClick={() => setShowGuideModal(true)}
          style={{
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            color: '#1d4ed8',
            borderRadius: '8px',
            padding: '4px 10px',
            fontSize: '0.78em',
            fontWeight: '700',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            transition: 'all 0.15s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#dbeafe';
            e.currentTarget.style.borderColor = '#93c5fd';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#eff6ff';
            e.currentTarget.style.borderColor = '#bfdbfe';
          }}
        >
          <span>ℹ️</span>
          <span>คู่มือระดับความรุนแรง & เกณฑ์ RCA</span>
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isClinicalOrCssd ? 'repeat(auto-fit, minmax(130px, 1fr))' : 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '8px',
          marginBottom: '10px'
        }}
      >
        {levels.map((item) => {
          const isSelected = value === item.code;
          return (
            <button
              key={item.code}
              type="button"
              disabled={disabled}
              onClick={() => onChange(item.code)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '10px 8px',
                borderRadius: '12px',
                border: isSelected
                  ? `2.5px solid ${item.color}`
                  : '1.5px solid #e2e8f0',
                backgroundColor: isSelected ? item.badgeBg : '#ffffff',
                color: isSelected ? item.textColor || item.color : '#334155',
                cursor: disabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s ease',
                boxShadow: isSelected
                  ? `0 4px 12px ${item.color}33`
                  : '0 1px 3px rgba(0,0,0,0.02)',
                position: 'relative'
              }}
            >
              <div
                style={{
                  fontSize: '1.2em',
                  fontWeight: '800',
                  color: isSelected ? item.color : '#1e293b',
                  marginBottom: '2px'
                }}
              >
                Level {item.code}
              </div>
              <div
                style={{
                  fontSize: '0.72em',
                  textAlign: 'center',
                  fontWeight: isSelected ? '700' : '500',
                  lineHeight: '1.25',
                  color: isSelected ? item.color : '#64748b'
                }}
              >
                {item.group ? item.group.split(' ')[0] : item.name}
              </div>
            </button>
          );
        })}
      </div>

      {/* กล่องแสดงคำอธิบายของระดับที่เลือกอยู่ในปัจจุบัน */}
      {(() => {
        const activeItem = levels.find((l) => l.code === value);
        if (!activeItem) {
          return (
            <div
              style={{
                padding: '12px 14px',
                borderRadius: '10px',
                backgroundColor: '#f8fafc',
                border: '1px dashed #cbd5e1',
                fontSize: '0.84em',
                color: '#64748b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '8px'
              }}
            >
              <span>👆 กรุณาคลิกเลือกระดับความรุนแรงด้านบนเพื่อดูคำจำกัดความและเกณฑ์ตอบสนอง</span>
              <button
                type="button"
                onClick={() => setShowGuideModal(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2563eb',
                  fontSize: '0.82em',
                  fontWeight: '700',
                  cursor: 'pointer',
                  padding: 0,
                  textDecoration: 'underline'
                }}
              >
                เปิดคู่มือเทียบเกณฑ์ทั้งหมด ↗
              </button>
            </div>
          );
        }

        return (
          <div
            style={{
              padding: '14px 16px',
              borderRadius: '12px',
              backgroundColor: activeItem.badgeBg,
              border: `1.5px solid ${activeItem.badgeBorder || activeItem.color}`,
              color: activeItem.textColor || '#1e293b',
              fontSize: '0.86em',
              lineHeight: '1.5'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: '6px',
                    backgroundColor: activeItem.color,
                    color: '#ffffff',
                    fontWeight: 'bold',
                    fontSize: '0.85em'
                  }}
                >
                  Level {activeItem.code}
                </span>
                <strong style={{ color: activeItem.color, fontSize: '0.95em' }}>
                  {activeItem.name}
                </strong>
              </div>

              {/* RCA Badge */}
              {activeItem.rca_required ? (
                <span
                  style={{
                    backgroundColor: '#fee2e2',
                    color: '#991b1b',
                    border: '1px solid #fca5a5',
                    padding: '2px 8px',
                    borderRadius: '6px',
                    fontSize: '0.76em',
                    fontWeight: '800'
                  }}
                >
                  ⚠️ ต้องทำ RCA ({activeItem.rca_timeline})
                </span>
              ) : (
                <span
                  style={{
                    backgroundColor: '#f0fdf4',
                    color: '#15803d',
                    border: '1px solid #bbf7d0',
                    padding: '2px 8px',
                    borderRadius: '6px',
                    fontSize: '0.76em',
                    fontWeight: '700'
                  }}
                >
                  🛡️ ไม่ต้องทำ RCA
                </span>
              )}
            </div>

            <div style={{ color: '#334155', fontSize: '0.88em', marginBottom: '8px' }}>
              {activeItem.description}
            </div>

            {/* 3 Metric Summary Boxes */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgba(0, 0, 0, 0.06)'
              }}
            >
              <div style={{ fontSize: '0.8em' }}>
                <strong style={{ color: '#0369a1' }}>⏱️ รักษา/แก้ไข:</strong>{' '}
                <span>{activeItem.action_timeline || '-'}</span>
              </div>
              <div style={{ fontSize: '0.8em' }}>
                <strong style={{ color: '#b45309' }}>📢 รายงาน:</strong>{' '}
                <span>{activeItem.reporting_timeline || '-'}</span>
              </div>
              <div style={{ fontSize: '0.8em' }}>
                <strong style={{ color: activeItem.rca_required ? '#b91c1c' : '#15803d' }}>
                  🔍 RCA:
                </strong>{' '}
                <span>{activeItem.rca_type || '-'}</span>
              </div>
            </div>

            <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowGuideModal(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: activeItem.color,
                  fontSize: '0.78em',
                  fontWeight: '700',
                  cursor: 'pointer',
                  padding: 0,
                  textDecoration: 'underline'
                }}
              >
                ดูคู่มือเกณฑ์ความรุนแรงและตัวอย่างเหตุการณ์ทั้งหมด ↗
              </button>
            </div>
          </div>
        );
      })()}

      {/* Guide Modal Popup */}
      <SeverityGuideModal
        isOpen={showGuideModal}
        onClose={() => setShowGuideModal(false)}
        initialTab={isClinicalOrCssd ? 'clinical' : 'non_clinical'}
        selectedLevel={value}
        onSelectLevel={onChange}
      />
    </div>
  );
};

export default DentalSeverityBadge;
